import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Filter, ChevronLeft, ChevronRight, X } from "lucide-react";
import { listSagas } from "../services/saga";
import type { ListSagasResult, SagaStatus } from "../types/saga";
import { PageHeader } from "../components/layout/PageHeader";
import { StatusPill } from "../components/saga/StatusPill";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import { RequireRole } from "../components/RequireRole";

const STATUS_OPTIONS: SagaStatus[] = [
  "RUNNING",
  "SUCCESS",
  "FAILED",
  "COMPENSATING",
  "FAILED_NEEDS_MANUAL",
];

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "— qualquer —" },
  ...STATUS_OPTIONS.map((s) => ({ value: s, label: s })),
];

const PAGE_SIZE = 25;

export default function SagaList() {
  const [data, setData] = useState<ListSagasResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState<SagaStatus | "">("");
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await listSagas({
          sagaType: filterType || undefined,
          status: filterStatus || undefined,
          limit: PAGE_SIZE,
          offset,
        });
        if (alive) {
          setData(r);
          setErr(null);
        }
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : "Erro ao listar sagas");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [filterType, filterStatus, offset]);

  const total = data?.pagination.total ?? 0;
  const pages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const hasFilters = !!filterType || !!filterStatus;

  return (
    <>
      <PageHeader
        title="Sagas"
        subtitle={`${total} execução${total === 1 ? "" : "ões"} no total`}
        actions={
          <RequireRole anyOf={["platform-master", "platform-admin", "platform-operations", "dev-on-call"]}>
            <Link to="/sagas/new">
              <Button size="sm">
                <Zap size={14} />
                Disparar saga
              </Button>
            </Link>
          </RequireRole>
        }
        toolbar={
          <div className="flex items-center gap-3 flex-wrap">
            <Filter size={14} className="text-muted-foreground" />
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Tipo</span>
              <Input
                value={filterType}
                onChange={(e) => { setFilterType(e.target.value); setOffset(0); }}
                placeholder="EchoSaga, OnboardTenant…"
                className="h-7 w-56 text-xs"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Status</span>
              <Select
                value={filterStatus}
                options={STATUS_FILTER_OPTIONS}
                onChange={(v) => { setFilterStatus(v as SagaStatus | ""); setOffset(0); }}
                className="w-44"
              />
            </div>
            {hasFilters && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => { setFilterType(""); setFilterStatus(""); setOffset(0); }}
              >
                <X size={12} />
                Limpar
              </Button>
            )}
          </div>
        }
      />

      <div className="p-6">
        {err && <div className="p-3 mb-4 rounded-md bg-danger-soft text-danger-soft-fg text-sm">{err}</div>}

        <div className="rounded-md border border-border bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-1 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left font-medium px-4 py-2 w-32">ID</th>
                <th className="text-left font-medium px-4 py-2">Tipo</th>
                <th className="text-left font-medium px-4 py-2">Status</th>
                <th className="text-left font-medium px-4 py-2 w-20">Step</th>
                <th className="text-left font-medium px-4 py-2 w-40">Iniciada</th>
                <th className="px-4 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {loading && (
                <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground italic">Carregando…</td></tr>
              )}
              {!loading && data && data.data.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground italic">Nenhuma saga encontrada.</td></tr>
              )}
              {!loading && data?.data.map((s) => (
                <tr key={s.sagaId} className="hover:bg-surface-2 transition-colors">
                  <td className="px-4 py-2.5">
                    <code className="font-mono-tight text-xs text-muted-foreground">{s.sagaId.slice(0, 8)}</code>
                  </td>
                  <td className="px-4 py-2.5 font-medium">{s.sagaType}</td>
                  <td className="px-4 py-2.5"><StatusPill status={s.status} size="xs" /></td>
                  <td className="px-4 py-2.5 font-mono-tight text-xs">{s.currentStep}/{s.totalSteps}</td>
                  <td className="px-4 py-2.5 text-muted-foreground font-mono-tight text-xs">
                    {new Date(s.startedAt).toLocaleString("pt-BR")}
                  </td>
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/sagas/${s.sagaId}`}
                      className="text-xs text-primary hover:underline whitespace-nowrap"
                    >
                      Ver →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm">
            <span className="text-xs text-muted-foreground font-mono-tight">
              página {currentPage} de {pages} · {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} de {total}
            </span>
            <div className="flex gap-2">
              <Button
                size="xs"
                variant="outline"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                <ChevronLeft size={12} />
                Anterior
              </Button>
              <Button
                size="xs"
                variant="outline"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Próxima
                <ChevronRight size={12} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
